// Rate App Component - Prompts users to rate on Play Store
import { useState, useEffect } from 'react';
import { Star, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const RATE_APP_KEY = 'salesspace-rate-prompted';
const PURCHASE_COUNT_KEY = 'salesspace-purchase-count';

export default function RateAppPrompt({ onClose }) {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [submitted, setSubmitted] = useState(false);

  const handleRate = (selectedRating) => {
    setRating(selectedRating);
    
    if (selectedRating >= 4) {
      // Good rating - redirect to Play Store
      window.open('https://play.google.com/store/apps/details?id=com.salesspace.pro', '_blank');
      localStorage.setItem(RATE_APP_KEY, 'rated');
      setSubmitted(true);
    } else {
      // Lower rating - show feedback form
      setSubmitted(true);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(RATE_APP_KEY, 'dismissed');
    onClose?.();
  };

  if (submitted) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 max-w-sm w-full text-center shadow-xl">
          <div className="text-4xl mb-4">🎉</div>
          <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
            {rating >= 4 ? 'Thank you!' : 'We appreciate your feedback'}
          </h3>
          <p className="text-slate-600 dark:text-slate-400 mb-4">
            {rating >= 4 
              ? 'Your support means a lot to us!'
              : 'We\'ll work hard to improve your experience.'}
          </p>
          <Button onClick={onClose} className="w-full">
            Continue Shopping
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 max-w-sm w-full shadow-xl relative">
        <button 
          onClick={handleDismiss}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="text-center">
          <div className="text-4xl mb-4">⭐</div>
          <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
            Enjoying SalesSpace?
          </h3>
          <p className="text-slate-600 dark:text-slate-400 mb-6">
            Tap a star to rate your experience
          </p>
          
          {/* Star Rating */}
          <div className="flex justify-center gap-2 mb-6">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => handleRate(star)}
                onMouseEnter={() => setHoveredRating(star)}
                onMouseLeave={() => setHoveredRating(0)}
                className="p-1 transition-transform hover:scale-110"
                data-testid={`rate-star-${star}`}
              >
                <Star 
                  className={`w-10 h-10 ${
                    star <= (hoveredRating || rating)
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-slate-300 dark:text-slate-600'
                  }`}
                />
              </button>
            ))}
          </div>
          
          <button
            onClick={handleDismiss}
            className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}

// Hook to check if we should show rate prompt
export function useRatePrompt() {
  const [showPrompt, setShowPrompt] = useState(false);

  const checkAndShowPrompt = () => {
    const alreadyPrompted = localStorage.getItem(RATE_APP_KEY);
    if (alreadyPrompted) return;

    const purchaseCount = parseInt(localStorage.getItem(PURCHASE_COUNT_KEY) || '0', 10);
    
    // Show after 2+ purchases
    if (purchaseCount >= 2) {
      setShowPrompt(true);
    }
  };

  const incrementPurchaseCount = () => {
    const current = parseInt(localStorage.getItem(PURCHASE_COUNT_KEY) || '0', 10);
    localStorage.setItem(PURCHASE_COUNT_KEY, String(current + 1));
  };

  const closePrompt = () => {
    setShowPrompt(false);
  };

  return {
    showPrompt,
    checkAndShowPrompt,
    incrementPurchaseCount,
    closePrompt
  };
}
